import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { ISubscription } from 'app/models/subscription';
import * as AppState from '../app.state';
import * as Actions from './actions'
import * as fromStripeSubscription from 'app/state/stripeSubscription/reducer';
import { of } from 'rxjs';
import * as fromStripePrice from 'app/state/stripePrice/reducer';
import * as fromStripeProduct from 'app/state/stripeProduct/reducer';
import * as fromRevenueCat from 'app/state/revenuecat/selectors';
import { ac } from 'vitest/dist/chunks/reporters.nr4dxCkA';
import { PurchasesEntitlementInfo } from '@revenuecat/purchases-capacitor';
import * as fromRevenuecatCustomer from 'app/state/revenuecat-customer/reducer';
import { IRevenuecatCustomer } from 'app/models/revenuecat-customer';
import { Premium } from '../../models/revenuecat-customer';

export interface State extends AppState.State {
  subscription?: ISubscription;
  /**
   * Distinguishes "not loaded yet" from "loaded, but does not exist" — the doc
   * stream emits undefined when the subscription document is missing, so
   * `subscription === undefined` alone cannot be used as a load signal.
   */
  subscriptionLoaded: boolean;
  error: string;
}

export const initialState: State = {
  loading: {},
  subscription: undefined,
  subscriptionLoaded: false,
  error: ''
};

export const stateName = 'subscriptions';
const getItemFeatureState = createFeatureSelector<State>(stateName);



export const reducer = createReducer<State>(
  initialState,
  on(Actions.loadItemSuccess, (state, action): State => {
    return {
      ...state,
      subscription: action.item,
      subscriptionLoaded: true,
      error: ''
    };
  }),
  on(Actions.loadItemFailure, (state, action): State => {
    return {
      ...state,
      subscription: undefined,
      subscriptionLoaded: true,
      error: action.error
    };
  }),
);





//Selectors
export const getItem = createSelector(
  getItemFeatureState,
  state => state.subscription
);

//Selectors
export const getHasPremium = createSelector(
  getItemFeatureState,
  fromStripeSubscription.selectAllItems,
  fromRevenuecatCustomer.selectCurrentItem,
  fromRevenueCat.selectActiveEntitlements,
  (state, stripeSubs, revenuecatCustomer, nativeEntitlement) => {

    let subDate = new Date(state.subscription?.billingPeriodEnds ?? '');

    if (!subDate || isNaN(subDate.getTime())) {
      subDate = new Date(0);
    }

    for (let item of stripeSubs) {
      const date = new Date(item.current_period_end);
      if (date > subDate) {
        subDate = date;
      }
    }

    // RevenueCat (Google Play / App Store): the Firestore-mirrored customer doc
    // from the RC extension (revenuecatCustomers/{uid}) — the only RC source on web.
    // expires_date comes as an ISO string from the extension.
    const rcExpiry = revenuecatCustomer?.entitlements?.Premium?.expires_date;
    if (rcExpiry) {
      const date = new Date(rcExpiry);
      if (!isNaN(date.getTime()) && date > subDate) {
        subDate = date;
      }
    }

    // Native RevenueCat SDK (Capacitor only): covers the gap right after purchase,
    // before the extension doc has synced. selectActiveEntitlements returns
    // {} when none exist — guard on isActive/expirationDate.
    const native = nativeEntitlement as PurchasesEntitlementInfo;
    if (native?.isActive && native?.expirationDate) {
      const date = new Date(native.expirationDate);
      if (!isNaN(date.getTime()) && date > subDate) {
        subDate = date;
      }
    }

    // NB: unsubscribe does NOT remove premium before expiry — the paid period runs out.
    return subDate > new Date();
  }
);

/**
 * Tri-state premium: null until the subscription document is loaded, then
 * boolean. Guards MUST use this (not getHasPremium) — otherwise premium users
 * are redirected on refresh/deep-link because getHasPremium emits false
 * synchronously before the Firestore state is in place.
 */
export const getHasPremiumOrNull = createSelector(
  getItemFeatureState,
  getHasPremium,
  (state, hasPremium): boolean | null => state.subscriptionLoaded ? hasPremium : null
);


//Selectors
export const getNewestSubscription = createSelector(
  getItemFeatureState,
  fromStripeSubscription.selectAllItems,
  fromStripePrice.selectAllEntities,
  fromStripeProduct.selectAllEntities,
  fromRevenueCat.selectActiveEntitlements,
  fromRevenuecatCustomer.selectCurrentItem,
  fromRevenuecatCustomer.selectIsUnsubscribed,
  (state, stripeSubs, prices, products, activeEntitlements1, revenuecatCustomer, isUnsubscribedRevenueCat) => {

    let subDate = new Date(state.subscription?.billingPeriodEnds ?? '');
    let retSub = state.subscription;
    if (retSub) {
      retSub = {
        ...retSub,
        name: (retSub.billingPeriodEnds ?? new Date()) < new Date() ? 'Free' : retSub.name,
        status: (retSub.billingPeriodEnds ?? new Date()) < new Date() ? 'Free' : retSub.status,
        paymentOrigin: 'barhelper',
      };
    }

    stripeSubs = stripeSubs.filter(x => x.status === 'active' || x.status === 'trialing');

    for (let item of stripeSubs) {
      subDate = new Date(1, 1, 1);
      const date = new Date(item.current_period_end);

      if (date >= subDate) {
        subDate = date;
        let amount = 0;
        let name = '';

        if (prices[item.price]?.unit_amount) {
          amount = prices[item.price]?.unit_amount ?? 0;
        }
        if (products[item.product]?.name) {
          name = products[item.product]?.name ?? item.status;
        }

        retSub = {
          billingPeriodEnds: new Date(item.current_period_end),
          status: item.status,
          barId: retSub?.barId ?? '',
          cancelAtPeriodEnd: item.cancel_at_period_end,
          orderId: item.id,
          id: retSub?.id ?? '',
          amount: amount.toString() ?? '0',
          name: name,
          paymentOrigin: 'stripe',

        }
      }
    }


    if (revenuecatCustomer) {
      if (revenuecatCustomer.entitlements.Premium) {
        retSub = {
          billingPeriodEnds: new Date(revenuecatCustomer.entitlements.Premium.expires_date),
          status: isUnsubscribedRevenueCat ? 'unsubscribed' : 'active',
          barId: retSub?.barId ?? '',
          cancelAtPeriodEnd: isUnsubscribedRevenueCat,
          orderId: revenuecatCustomer.id ?? '',
          id: retSub?.id ?? '',
          name: revenuecatCustomer.entitlements.Premium.product_identifier == 'ios_yearly' ? 'Yearly Subscription'
            : revenuecatCustomer.entitlements.Premium.product_identifier == 'ios_monthly' ? 'Monthly Subscription'
              : revenuecatCustomer.entitlements.Premium.product_identifier == 'android_yearly' ? 'Yearly Subscription'
                : revenuecatCustomer.entitlements.Premium.product_identifier == 'android_monthly' ? 'Monthly Subscription'
                  : revenuecatCustomer.entitlements.Premium.product_identifier,
          paymentOrigin: 'revenuecatCustomer',
        }

      }
    }

    if (activeEntitlements1 && !revenuecatCustomer) {
      const entitlement = activeEntitlements1 as PurchasesEntitlementInfo;

      if (entitlement.isActive && entitlement.expirationDate) {
        retSub = {
          billingPeriodEnds: new Date(entitlement.expirationDate),
          status: isUnsubscribedRevenueCat ? 'unsubscribed' : 'active',
          barId: retSub?.barId ?? '',
          cancelAtPeriodEnd: entitlement.expirationDate ? new Date(entitlement.expirationDate) < new Date() : false,
          orderId: entitlement.identifier,
          id: retSub?.id ?? '',
          name: entitlement.productIdentifier == 'ios_yearly' ? 'Yearly Subscription'
            : entitlement.productIdentifier == 'ios_monthly' ? 'Monthly Subscription'
              : entitlement.productIdentifier == 'android_yearly' ? 'Yearly Subscription'
                : entitlement.productIdentifier == 'android_monthly' ? 'Monthly Subscription'
                  : entitlement.productIdentifier,
          paymentOrigin: 'revenuecatNative',
        }

      }
    }





    return retSub;
  }
);


export const hasValidSubscription = createSelector(
  fromStripeSubscription.selectAllItems,
  getItem,
  ((stripeSubs, state) => {
    return (state &&
      (state.status == 'Active' || (state.status == 'Trial') || (state.status == 'Custom') &&
        state.billingPeriodEnds && state.billingPeriodEnds > new Date()) ||
      !!stripeSubs.find(x => x.status === 'active'))
  })
);

